import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { WordUsageDisplay } from '../types/wordUsage';

interface WordUsageTableProps {
  words: WordUsageDisplay[];
  title: string;
}

const WordUsageTable: React.FC<WordUsageTableProps> = ({ words, title }) => {
  // No need to consolidate words anymore, they're already in the right format
  const consolidatedWords = useMemo(() => {
    // Sort by total count
    return [...words].sort((a, b) => b.totalCount - a.totalCount);
  }, [words]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getForgeTypeBadge = (forgeType: string) => {
    switch (forgeType) {
      case 'Item':
        return (
          <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
            Item
          </Badge>
        );
      case 'Location':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
            Location
          </Badge>
        );
      case 'Character':
        return (
          <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
            Character
          </Badge>
        );
      case 'Goal':
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
            Goal
          </Badge>
        );
      case 'Deconstructor':
        return (
          <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
            Deconstructor
          </Badge>
        );
      default:
        return <Badge variant="outline">{forgeType}</Badge>;
    }
  };

  // Helper function to render multiple forge type badges
  const renderForgeTypeBadges = (forgeTypes: string[]) => {
    return (
      <div className="flex flex-wrap gap-1">
        {forgeTypes.map((forgeType, index) => (
          <span key={index}>{getForgeTypeBadge(forgeType)}</span>
        ))}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-border shadow-sm bg-card overflow-hidden">
      <div className="p-4 border-b">
        <h3 className="text-lg font-medium">{title}</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Word</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Input</TableHead>
            <TableHead>Output</TableHead>
            <TableHead>Deconstructor</TableHead>
            <TableHead>Goal</TableHead>
            <TableHead>Forge Types</TableHead>
            <TableHead>Last Used</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedWords.map((word, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">{word.word}</TableCell>
              <TableCell>{word.totalCount}</TableCell>
              <TableCell>{word.inputCount > 0 ? word.inputCount : '-'}</TableCell>
              <TableCell>{word.outputCount > 0 ? word.outputCount : '-'}</TableCell>
              <TableCell>{word.deconstructorCount > 0 ? word.deconstructorCount : '-'}</TableCell>
              <TableCell>-</TableCell> {/* No more goal count */}
              <TableCell>{renderForgeTypeBadges(word.forgeTypes)}</TableCell>
              <TableCell>{formatDate(word.lastUsed)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default WordUsageTable;
